import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useSocketStore } from './socket';
import { AudioRecorder, AudioLevelMonitor } from '../services/AudioRecorder';
import { wakeWordService } from '../services/WakeWordService';
import { playAudioUrl } from '../services/audioPlayback';
import { muteAllForRecording, unmuteAfterRecording } from '../services/audioMuter';

export type RecordingQuality = 'low' | 'medium' | 'high' | 'max';

const QUALITY_BITRATE: Record<RecordingQuality, number> = {
  low: 16_000,
  medium: 32_000,
  high: 64_000,
  max: 128_000,
};

const BLUETOOTH_MIC_REGEX =
  /bluetooth|airpods|buds|jabra|beats|bose|sony wh|wf-|wh-|galaxy buds|hands-free|hfp|headset/i;

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  intent?: string;
  confidence?: number;
  source?: 'gemini' | 'local';
  action?: AgentAction;
}

/**
 * An action the agent asked the hub to run, when its answer carried one. The
 * hub sends its own envelope and the UI only logs it today, so this names the
 * two fields it is identified by and leaves the action's own payload open.
 */
export interface AgentAction {
  type?: string;
  name?: string;
  [key: string]: unknown;
}

export interface AgentConversation {
  id: string;
  userId: string;
  messages: AgentMessage[];
  createdAt: Date;
  updatedAt: Date;
}

export interface AgentConfig {
  enabled: boolean;
  wakeWord: string;
  language: string;
  voice: string;
  autoListen: boolean;
  confirmActions: boolean;
  responseLevel: 'short' | 'full';
}

export const useAgentStore = defineStore('agent', () => {
  const messages = ref<AgentMessage[]>([]);
  const currentConversation = ref<AgentConversation | null>(null);
  const isListening = ref(false);
  const isProcessing = ref(false);
  const isSpeaking = ref(false);
  const config = ref<AgentConfig>({
    enabled: true,
    wakeWord: 'hey_torik',
    language: 'en',
    voice: 'en_US-lessac-medium',
    autoListen: false,
    confirmActions: true,
    responseLevel: 'short',
  });

  const socketStore = useSocketStore();
  let audioRecorder: AudioRecorder | null = null;
  let audioLevelMonitor: AudioLevelMonitor | null = null;
  let lastRecordingStopTime = 0;
  const audioLevel = ref(0);
  const recordingError = ref<string | null>(null);
  const initialized = ref(false);
  let silenceTimeout: ReturnType<typeof setTimeout> | null = null;

  const SILENCE_LEVEL_THRESHOLD = 6;
  const SILENCE_TIMEOUT_MS = 2500;

  function clearSilenceTimer() {
    if (silenceTimeout) {
      clearTimeout(silenceTimeout);
      silenceTimeout = null;
    }
  }

  function cleanupAudioMonitoring() {
    clearSilenceTimer();
    if (audioLevelMonitor) {
      audioLevelMonitor.destroy();
      audioLevelMonitor = null;
    }
    audioLevel.value = 0;
  }

  function releaseRecorder() {
    if (!audioRecorder) return;
    try {
      audioRecorder.destroy();
    } catch (error) {
      console.warn('[AgentStore] Recorder destroy warning:', error);
    }
    audioRecorder = null;
  }

  function scheduleSilenceAutoStop() {
    clearSilenceTimer();
    silenceTimeout = setTimeout(() => {
      if (!isListening.value) return;
      console.log('[AgentStore] Silence timeout reached, auto-stopping recording');
      stopListening();
    }, SILENCE_TIMEOUT_MS);
  }

  // ── Wake Word state ─────────────────────────────────────────────────
  const wakeWordEnabled = ref(
    typeof localStorage !== 'undefined' && localStorage.getItem('wakeWordEnabled') === 'true',
  );
  const wakeWordActive = ref(false); // mic streaming for detection
  const wakeWordDetected = ref(false); // flash state after detection
  const wakeWordListening = ref(false); // capturing command audio
  const wakeWordProcessing = ref(false); // STT + agent processing
  const wakeWordAudioLevel = ref(0);
  const wakeWordMicWarning = ref('');
  let wakeWordStartAttempting = false;
  let wakeWordUserInitiated = false;
  let wakeWordRetryTimer: ReturnType<typeof setInterval> | null = null;

  // ── Dog commands (from voice) ───────────────────────────────────────────
  const dogCommand = ref<string | null>(null);

  // ── Recording quality & mute settings ──────────────────────────────────
  const recordingQuality = ref<RecordingQuality>(
    (typeof localStorage !== 'undefined'
      ? (localStorage.getItem('recordingQuality') as RecordingQuality | null)
      : null) || 'high',
  );
  const muteOnRecord = ref<boolean>(
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('muteOnRecord') !== 'false' // default true
      : true,
  );

  function setRecordingQuality(quality: RecordingQuality) {
    recordingQuality.value = quality;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('recordingQuality', quality);
    }
    // Destroy existing recorder so it's recreated with new bitrate
    if (audioRecorder) {
      try {
        audioRecorder.cancelRecording();
      } catch {
        /* ignore */
      }
      audioRecorder = null;
    }
  }

  function setMuteOnRecord(enabled: boolean) {
    muteOnRecord.value = enabled;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('muteOnRecord', String(enabled));
    }
  }

  // ── Audio Input Device selection ─────────────────────────────────────
  const audioInputDevices = ref<MediaDeviceInfo[]>([]);
  const selectedAudioDeviceId = ref<string>(
    typeof localStorage !== 'undefined' ? localStorage.getItem('selectedAudioDeviceId') || '' : '',
  );

  async function loadAudioDevices(requestPermission = false) {
    try {
      // Getting labels requires (at least) one granted mic permission. Only
      // probe when explicitly requested (real capture / user action) — never
      // on page mount, so the mic indicator doesn't turn on just from loading.
      if (requestPermission && !audioRecorder) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
        if (stream) stream.getTracks().forEach((t) => t.stop());
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      audioInputDevices.value = devices.filter((d) => d.kind === 'audioinput');
    } catch (e) {
      console.warn('[AgentStore] Could not enumerate audio devices:', e);
    }
  }

  /**
   * Resolve the deviceId to use for recording.
   * If the user picked one explicitly, honour it.
   * Otherwise, prefer a built-in (non-Bluetooth) mic so that BT headphones
   * stay in A2DP (high-quality playback) instead of switching to HFP
   * (low-quality bidirectional), which ruins both recording and playback.
   */
  async function resolveRecordingDeviceId(): Promise<string | undefined> {
    // User explicitly chose a device → use it as-is
    if (selectedAudioDeviceId.value) return selectedAudioDeviceId.value;

    try {
      if (audioInputDevices.value.length === 0) await loadAudioDevices(true);
      // If we only got unlabeled devices (no mic permission yet), probe once
      // for labels so we can still pick the full-quality endpoint.
      if (!audioInputDevices.value.some((d) => d.label)) {
        await loadAudioDevices(true);
      }
      const devices = audioInputDevices.value;
      if (devices.length <= 1) return undefined; // only one mic, nothing to choose

      const BT_KEYWORDS = /bluetooth|airpods|buds|jabra|beats|bose|sony wh|wf-|wh-|galaxy buds/i;
      // Windows exposes a narrowband "Communications - Microphone" (16 kHz,
      // call-grade) next to each real mic. Prefer the full-quality endpoint;
      // narrowband audio noticeably degrades Whisper transcription.
      const quality = devices.find(
        (d) => d.label && !/communications/i.test(d.label) && !BT_KEYWORDS.test(d.label) && d.deviceId !== 'default',
      );
      if (quality) {
        console.log(`[AgentStore] Auto-selected full-quality mic: "${quality.label}"`);
        return quality.deviceId;
      }
      const builtin = devices.find((d) => d.label && !BT_KEYWORDS.test(d.label) && d.deviceId !== 'default');
      if (builtin) {
        console.log(`[AgentStore] Auto-selected non-BT mic: "${builtin.label}"`);
        return builtin.deviceId;
      }
    } catch {
      /* fall through */
    }
    return undefined; // browser default
  }

  function setAudioDevice(deviceId: string) {
    selectedAudioDeviceId.value = deviceId;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('selectedAudioDeviceId', deviceId);
    }
    // Destroy existing recorder so it's recreated with the new device next time
    if (audioRecorder) {
      try {
        audioRecorder.cancelRecording();
      } catch {
        /* ignore */
      }
      audioRecorder = null;
    }
  }

  const selectedAudioDeviceInfo = computed(() => {
    if (audioInputDevices.value.length === 0) return null;
    if (selectedAudioDeviceId.value) {
      return audioInputDevices.value.find((d) => d.deviceId === selectedAudioDeviceId.value) || null;
    }
    return audioInputDevices.value.find((d) => d.deviceId === 'default') || null;
  });

  const bluetoothMicLikelyLowQuality = computed(() => {
    const selected = selectedAudioDeviceInfo.value;
    if (!selected?.label) return false;
    return BLUETOOTH_MIC_REGEX.test(selected.label);
  });

  async function preferBuiltInMicForQuality() {
    if (audioInputDevices.value.length === 0) {
      await loadAudioDevices(true);
    }
    const builtIn = audioInputDevices.value.find(
      (d) =>
        d.deviceId !== 'default' && d.label && !/communications/i.test(d.label) && !BLUETOOTH_MIC_REGEX.test(d.label),
    );
    if (builtIn) {
      setAudioDevice(builtIn.deviceId);
      return;
    }
    // Fall back to system default if we cannot identify a built-in mic explicitly.
    setAudioDevice('');
  }

  // Prompt history — shared between FAB and Agent view
  const promptHistory = ref<string[]>([]);
  function pushToHistory(text: string) {
    promptHistory.value.push(text);
  }

  // Computed
  const hasMessages = computed(() => messages.value.length > 0);
  const lastMessage = computed(() => messages.value[messages.value.length - 1]);

  // Watch for socket connection and auto-initialize
  watch(
    () => socketStore.socket,
    (socket) => {
      if (socket) {
        console.log('[AgentStore] Socket changed, reinitializing listeners...');
        initialized.value = false; // Reset so listeners get re-attached
        initialize();
      }
    },
    { immediate: true },
  );

  // Initialize agent Socket.IO listeners
  function initialize() {
    const socket = socketStore.socket;
    if (!socket) {
      console.log('[AgentStore] Socket not available yet');
      return;
    }

    if (initialized.value) {
      console.log('[AgentStore] Already initialized, skipping');
      return;
    }

    console.log('[AgentStore] Initializing agent Socket.IO listeners...');

    // Listen for agent responses
    socket.on('agent:response', (data: { message?: AgentMessage }) => {
      console.log('[AgentStore] Received agent:response:', data);
      if (data && data.message) {
        addMessage(data.message);
        isProcessing.value = false;
      } else {
        console.warn('[AgentStore] Invalid response format:', data);
      }
    });

    // Listen for agent actions
    socket.on('agent:action', (data: { action?: AgentAction }) => {
      console.log('[AgentStore] Agent action:', data);
      // Handle action feedback
    });

    // Listen for typing indicator
    socket.on('agent:typing', (data: { isTyping: boolean }) => {
      console.log('[AgentStore] Agent typing:', data.isTyping);
      isProcessing.value = data.isTyping;
    });

    // Listen for agent errors
    socket.on('agent:error', (data: { error: string }) => {
      console.error('[AgentStore] Agent error:', data);
      isProcessing.value = false;
      addMessage({
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: `Error: ${data.error}`,
        timestamp: new Date(),
      });
    });

    socket.on('agent:cancelled', () => {
      console.log('[AgentStore] Agent request cancelled');
      isProcessing.value = false;
    });

    // Listen for conversation updates
    socket.on('agent:conversation', (data: { conversation: AgentConversation }) => {
      console.log('[AgentStore] Conversation updated:', data);
      currentConversation.value = data.conversation;
      messages.value = data.conversation.messages;
    });

    // Listen for dog commands from agent
    socket.on('agent:dog_command', (data: { command: string }) => {
      console.log('[AgentStore] Dog command:', data.command);
      dogCommand.value = data.command;
      // Auto-clear so a re-trigger of the same command still fires the watcher
      setTimeout(() => {
        dogCommand.value = null;
      }, 100);
    });

    // Listen for UI navigation commands from agent
    socket.on('agent:navigate', (data: { route: string; label?: string; entityName?: string }) => {
      console.log('[AgentStore] Navigate:', data);
      import('../router').then(({ default: router }) => {
        router.push(data.route);
      });
    });

    // ── Wake word events ────────────────────────────────────────────────
    socket.on('agent:wakeword:detected', (data: { keyword?: string; score?: number }) => {
      console.log('[AgentStore] Wake word detected:', data);
      // Switch to lossless command capture (no speech gating).
      wakeWordService.beginCommandCapture();
      wakeWordDetected.value = true;
      wakeWordListening.value = true;
      // Auto-clear flash after a short delay
      setTimeout(() => {
        wakeWordDetected.value = false;
      }, 2000);
    });

    socket.on('agent:wakeword:processing', () => {
      console.log('[AgentStore] Wake word processing (STT + agent)...');
      wakeWordListening.value = false;
      wakeWordProcessing.value = true;
    });

    socket.on('agent:wakeword:done', () => {
      console.log('[AgentStore] Wake word cycle complete');
      wakeWordService.endCommandCapture();
      wakeWordListening.value = false;
      wakeWordProcessing.value = false;
      // Ensure the AudioContext didn't get suspended (e.g. by TTS playback)
      // and that audio is flowing again so the next wake word can be detected.
      if (wakeWordEnabled.value && wakeWordActive.value) {
        wakeWordService.resume();
      }
    });

    socket.on('agent:wakeword:error', (data: { error: string }) => {
      console.warn('[AgentStore] Wake word error:', data.error);
      wakeWordService.endCommandCapture();
      wakeWordListening.value = false;
      wakeWordProcessing.value = false;
    });

    // TTS playback — server sends a WAV URL after every assistant reply.
    // After recording stops, Bluetooth headphones (AirPods etc.) need ~1.5 s
    // to switch from HFP (bidirectional mic) back to A2DP (high-quality playback).
    // Delay TTS playback if recording recently ended to avoid garbled audio.
    const BT_PROFILE_SWITCH_MS = 1500;
    socket.on('agent:tts', async (data: { url: string }) => {
      console.log('[AgentStore] TTS audio received:', data.url);
      const elapsed = Date.now() - lastRecordingStopTime;
      if (elapsed < BT_PROFILE_SWITCH_MS) {
        const wait = BT_PROFILE_SWITCH_MS - elapsed;
        console.log(`[AgentStore] Delaying TTS ${wait}ms for Bluetooth profile switch`);
        await new Promise((r) => {
          setTimeout(r, wait);
        });
      }
      playAudioUrl(data.url, 'normal').catch((err) => console.warn('[AgentStore] TTS playback error:', err));
    });

    // Bind WakeWordService to socket
    wakeWordService.setSocket(socket);

    initialized.value = true;
    console.log('[AgentStore] Initialized agent Socket.IO listeners');
  }

  // Send message to agent
  function sendMessage(content: string) {
    const socket = socketStore.socket;

    console.log('[AgentStore] sendMessage called with:', content);
    console.log('[AgentStore] Socket available:', !!socket);
    console.log('[AgentStore] Socket connected:', socket?.connected);
    console.log('[AgentStore] Socket ID:', socket?.id);

    if (!socket || !socket.connected) {
      console.error('[AgentStore] Socket not connected');
      isProcessing.value = false;
      addMessage({
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: 'Not connected to server. Please refresh the page.',
        timestamp: new Date(),
      });
      return;
    }

    // Add user message immediately
    const userMessage: AgentMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    addMessage(userMessage);

    // Send to server
    console.log('[AgentStore] Emitting agent:message event...');
    isProcessing.value = true;
    socket.emit('agent:message', { message: content });
    console.log('[AgentStore] Message emitted successfully');

    // Add timeout to prevent permanent disabled state
    setTimeout(() => {
      if (isProcessing.value) {
        console.warn('[AgentStore] Message timeout - resetting processing state');
        console.warn('[AgentStore] Socket connected at timeout:', socket.connected);
        isProcessing.value = false;
        addMessage({
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: 'Request timed out. Please try again.',
          timestamp: new Date(),
        });
      }
    }, 45000); // Increased to 45 seconds from 30
  }

  // Send voice input to agent
  function sendVoiceInput(audioData: ArrayBuffer) {
    const socket = socketStore.socket;
    if (!socket || !socket.connected) {
      console.error('[AgentStore] Socket not connected');
      return;
    }

    isProcessing.value = true;
    socket.emit('agent:voice', { audio: audioData });
  }

  // Start listening for voice input
  async function startListening() {
    const socket = socketStore.socket;

    // Prevent multiple simultaneous recordings
    if (isListening.value) {
      console.warn('[AgentStore] Already listening');
      return;
    }

    if (!socket || !socket.connected) {
      console.error('[AgentStore] Socket not connected');
      recordingError.value = 'Not connected to server';
      return;
    }

    if (!AudioRecorder.isSupported()) {
      console.error('[AgentStore] Audio recording not supported');
      recordingError.value = 'Audio recording not supported in this browser';
      return;
    }

    try {
      recordingError.value = null;
      cleanupAudioMonitoring();

      // Always recreate recorder so selected device and constraints are fresh.
      releaseRecorder();

      // Mute all audio sources to prevent bleed into microphone
      if (muteOnRecord.value) {
        muteAllForRecording();
      }

      // Create new audio recorder if needed
      if (!audioRecorder) {
        const deviceId = await resolveRecordingDeviceId();
        audioRecorder = new AudioRecorder({
          channelCount: 1,
          audioBitsPerSecond: QUALITY_BITRATE[recordingQuality.value],
          deviceId,
        });

        // Audio chunks are collected internally by MediaRecorder;
        // the complete blob is sent with agent:voice:stop — no need to stream chunks.

        // Handle errors
        audioRecorder.on('error', (error) => {
          console.error('[AgentStore] Audio recorder error:', error);
          recordingError.value = error.message;
          isListening.value = false;
        });

        // Handle permission denied
        audioRecorder.on('permission-denied', () => {
          console.error('[AgentStore] Microphone permission denied');
          recordingError.value = 'Microphone permission denied. Please allow microphone access.';
          isListening.value = false;
        });
      }

      // Pause wake word streaming while manual recording is active so the
      // wake word service cannot trigger a second command in the background.
      if (wakeWordActive.value) {
        wakeWordService.pause();
      }

      // Start recording
      await audioRecorder.startRecording();
      isListening.value = true;

      audioLevelMonitor = audioRecorder.createAudioLevelMonitor();
      if (audioLevelMonitor) {
        audioLevelMonitor.startMonitoring((level) => {
          audioLevel.value = level;
          if (!isListening.value) return;
          if (level <= SILENCE_LEVEL_THRESHOLD) {
            if (!silenceTimeout) scheduleSilenceAutoStop();
          } else {
            clearSilenceTimer();
          }
        });
      }
      scheduleSilenceAutoStop();

      // Notify server that voice session started
      socket.emit('agent:voice:start');

      console.log('[AgentStore] Voice recording started');
    } catch (__caught: unknown) {
      const error = __caught as Error;
      console.error('[AgentStore] Failed to start listening:', error);
      recordingError.value = error.message || 'Failed to start recording';
      isListening.value = false;
    }
  }

  // Stop listening for voice input
  async function stopListening() {
    const socket = socketStore.socket;

    if (!audioRecorder) {
      console.warn('[AgentStore] No audio recorder instance');
      isListening.value = false;
      return;
    }

    if (!isListening.value) {
      console.warn('[AgentStore] Not currently listening');
      return;
    }

    // Update UI state immediately to prevent button blocking
    isListening.value = false;
    isProcessing.value = true;
    console.log('[AgentStore] Stopping voice recording...');

    try {
      // Stop recording and get final audio blob (non-blocking with timeout)
      const audioBlob = await audioRecorder.stopRecording();

      console.log('[AgentStore] Recording stopped, blob size:', audioBlob?.size || 0, 'bytes');

      if (socket && socket.connected) {
        // Send complete audio blob with stop event to avoid race condition
        // where chunked audio hasn't fully arrived yet
        let audioData: ArrayBuffer | undefined;
        if (audioBlob && audioBlob.size > 0) {
          try {
            audioData = await audioBlob.arrayBuffer();
            console.log('[AgentStore] Complete audio blob converted:', audioData.byteLength, 'bytes');
          } catch {
            console.warn('[AgentStore] Failed to convert audio blob, relying on chunks');
          }
        }
        socket.emit('agent:voice:stop', audioData ? { audioData: new Uint8Array(audioData) } : {});
        console.log('[AgentStore] Voice stop signal sent to server');
      } else {
        console.warn('[AgentStore] Socket not connected, cannot send voice data');
        recordingError.value = 'Connection lost. Please try again.';
        isProcessing.value = false;
      }

      // Note: The transcript will arrive via 'agent:response' event
    } catch (__caught: unknown) {
      const error = __caught as Error;
      console.error('[AgentStore] Failed to stop listening:', error);
      recordingError.value = error.message || 'Failed to stop recording';
      isProcessing.value = false;
    } finally {
      cleanupAudioMonitoring();
      releaseRecorder();
      // Record when the mic was released so TTS playback can wait for
      // Bluetooth HFP→A2DP profile switch before playing audio.
      lastRecordingStopTime = Date.now();
      // Restore audio after recording stops
      unmuteAfterRecording();
      // Resume wake word streaming now that manual recording has finished.
      if (wakeWordEnabled.value && wakeWordActive.value) {
        wakeWordService.resume();
      }
    }
  }

  // Force cancel recording (emergency stop)
  function cancelRecording() {
    console.log('[AgentStore] Force cancelling recording');

    if (audioRecorder) {
      try {
        audioRecorder.cancelRecording();
      } catch (error) {
        console.error('[AgentStore] Error cancelling recorder:', error);
      }
    }

    isListening.value = false;
    isProcessing.value = false;
    recordingError.value = null;
    cleanupAudioMonitoring();
    releaseRecorder();
    lastRecordingStopTime = Date.now();
    unmuteAfterRecording();
    // Resume wake word streaming after cancelled recording.
    if (wakeWordEnabled.value && wakeWordActive.value) {
      wakeWordService.resume();
    }

    const socket = socketStore.socket;
    if (socket && socket.connected) {
      socket.emit('agent:voice:stop');
    }
  }

  function cancelProcessing() {
    const socket = socketStore.socket;
    if (isListening.value) {
      cancelRecording();
      return;
    }
    isProcessing.value = false;
    if (socket && socket.connected) {
      socket.emit('agent:cancel');
    }
  }

  // Add message to conversation
  function addMessage(message: AgentMessage) {
    messages.value.push(message);
  }

  // Clear conversation
  function clearConversation() {
    messages.value = [];
    currentConversation.value = null;
    isProcessing.value = false; // Reset processing state
  }

  // Load conversation by ID
  async function loadConversation(conversationId: string) {
    try {
      const response = await fetch(`/api/agent/conversations/${conversationId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load conversation');
      }

      const conversation = await response.json();
      currentConversation.value = conversation;
      messages.value = conversation.messages;
    } catch (error) {
      console.error('[AgentStore] Error loading conversation:', error);
    }
  }

  // Load conversation history
  async function loadHistory() {
    try {
      const response = await fetch('/api/agent/conversations', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load conversation history');
      }

      return await response.json();
    } catch (error) {
      console.error('[AgentStore] Error loading history:', error);
      return [];
    }
  }

  // Update configuration
  function updateConfig(updates: Partial<AgentConfig>) {
    config.value = { ...config.value, ...updates };
    localStorage.setItem('agentConfig', JSON.stringify(config.value));
  }

  // Load configuration from localStorage
  function loadConfig() {
    const saved = localStorage.getItem('agentConfig');
    if (saved) {
      try {
        config.value = { ...config.value, ...JSON.parse(saved) };
      } catch (error) {
        console.error('[AgentStore] Error loading config:', error);
      }
    }
  }

  // Initialize config on store creation
  loadConfig();

  // ── Always-listening (panel mode) ─────────────────────────────────────────

  // ── Wake Word enable / disable ────────────────────────────────────────
  async function enableWakeWord() {
    wakeWordEnabled.value = true;
    localStorage.setItem('wakeWordEnabled', 'true');
    wakeWordUserInitiated = true;
    await startWakeWordStreaming();
  }

  async function disableWakeWord() {
    wakeWordEnabled.value = false;
    localStorage.setItem('wakeWordEnabled', 'false');
    wakeWordUserInitiated = false;
    await stopWakeWordStreaming();
  }

  async function startWakeWordStreaming() {
    if (wakeWordActive.value) return;
    const socket = socketStore.socket;
    if (!socket?.connected) {
      console.warn('[AgentStore] Cannot start wake word — socket not connected');
      return;
    }
    wakeWordService.setSocket(socket);

    // Wire up level changes
    wakeWordService.removeAllListeners();
    wakeWordService.on('level-change', (level) => {
      wakeWordAudioLevel.value = level;
      if (level > 0.05) wakeWordMicWarning.value = '';
    });
    wakeWordService.on('no-audio', (reason: string) => {
      wakeWordMicWarning.value = reason;
    });
    wakeWordService.on('error', (err) => {
      console.error('[AgentStore] Wake word mic error:', err);
      wakeWordActive.value = false;
    });
    wakeWordService.on('permission-denied', () => {
      console.error('[AgentStore] Wake word mic permission denied');
      wakeWordActive.value = false;
      if (wakeWordUserInitiated) {
        // User explicitly toggled it on and the browser blocked the mic.
        wakeWordEnabled.value = false;
        localStorage.setItem('wakeWordEnabled', 'false');
        wakeWordMicWarning.value = '';
      } else {
        // Auto-start after reload can be denied until a user gesture; keep the
        // armed state and let the retry timer / next interaction pick it up.
        wakeWordMicWarning.value = 'Microphone needs permission — tap “Wake word” to start it.';
      }
    });

    const deviceId = await resolveRecordingDeviceId();
    if (deviceId) {
      const dev = audioInputDevices.value.find((d) => d.deviceId === deviceId);
      console.log(`[AgentStore] Wake word mic: ${dev?.label || deviceId}`);
    } else {
      console.log('[AgentStore] Wake word mic: system default');
    }
    await wakeWordService.start(deviceId);
    wakeWordActive.value = true;
  }

  async function stopWakeWordStreaming() {
    await wakeWordService.stop();
    wakeWordActive.value = false;
    wakeWordDetected.value = false;
    wakeWordListening.value = false;
    wakeWordProcessing.value = false;
    wakeWordAudioLevel.value = 0;
    wakeWordMicWarning.value = '';
  }

  function cancelWakeWordCapture() {
    if (!wakeWordListening.value && !wakeWordProcessing.value) return;
    console.log('[AgentStore] Cancelling wake word capture');
    const socket = socketStore.socket;
    if (socket?.connected) {
      socket.emit('agent:wakeword:cancel');
    }
    wakeWordDetected.value = false;
    wakeWordListening.value = false;
    wakeWordProcessing.value = false;
  }

  // ── Auto-start: keep the armed wake word running across reloads ──────────
  async function ensureWakeWordRunning() {
    if (!wakeWordEnabled.value || wakeWordActive.value || wakeWordStartAttempting) {
      return;
    }
    const socket = socketStore.socket;
    if (!socket?.connected) return; // retried by the timer once connected
    wakeWordStartAttempting = true;
    wakeWordUserInitiated = false;
    try {
      await startWakeWordStreaming();
    } catch (e) {
      console.warn('[AgentStore] Wake word auto-start failed:', e);
    } finally {
      wakeWordStartAttempting = false;
    }
  }

  // Retry until it starts: covers the socket connecting after the store
  // initializes, the browser wanting a user gesture, or a transient mic error.
  if (!wakeWordRetryTimer) {
    wakeWordRetryTimer = setInterval(() => {
      void ensureWakeWordRunning();
    }, 3000);
  }

  // Immediate + reactive start whenever the socket is connected.
  watch(
    () => socketStore.socket?.connected,
    (connected) => {
      if (connected) void ensureWakeWordRunning();
    },
    { immediate: true },
  );

  /**
   * When `alwaysListening` is true (enabled in panel mode), the store
   * continuously monitors the microphone for the wake word "hey otto"
   * using a simple energy-based detection fallback (real keyword spotting
   * requires a dedicated on-device model and would live in a Capacitor plugin).
   *
   * For now we implement continuous listen → send → respond → restart cycle
   * so the panel is always ready after the previous reply finishes.
   */
  const alwaysListening = ref(false);
  let alwaysListenRestart: ReturnType<typeof setTimeout> | null = null;

  watch([alwaysListening, () => socketStore.socket?.connected], ([active]) => {
    if (active) {
      scheduleAlwaysListen();
    }
  });

  watch(isProcessing, (processing) => {
    if (!processing && alwaysListening.value && !isListening.value) {
      scheduleAlwaysListen(1500);
    }
  });

  watch(isSpeaking, (speaking) => {
    if (!speaking && alwaysListening.value && !isListening.value && !isProcessing.value) {
      scheduleAlwaysListen(1000);
    }
  });

  function scheduleAlwaysListen(delay = 500) {
    if (alwaysListenRestart) clearTimeout(alwaysListenRestart);
    alwaysListenRestart = setTimeout(async () => {
      if (alwaysListening.value && !isListening.value && !isProcessing.value && !isSpeaking.value) {
        const socket = socketStore.socket;
        if (socket?.connected) {
          await startListening();
          // Auto-stop after 8 seconds of silence (server-side VAD may stop it earlier)
          alwaysListenRestart = setTimeout(() => {
            if (isListening.value) stopListening();
          }, 8000);
        }
      }
    }, delay);
  }

  function enableAlwaysListening() {
    alwaysListening.value = true;
    // Wake word is fixed to "hey torik"
    config.value = { ...config.value, wakeWord: 'hey_torik' };
  }

  function disableAlwaysListening() {
    alwaysListening.value = false;
    if (alwaysListenRestart) {
      clearTimeout(alwaysListenRestart);
      alwaysListenRestart = null;
    }
    if (isListening.value) cancelRecording();
  }

  // Reset processing state (useful for debugging/recovery)
  function resetProcessing() {
    isProcessing.value = false;
    console.log('[AgentStore] Processing state manually reset');
  }

  return {
    // State
    messages,
    currentConversation,
    isListening,
    isProcessing,
    isSpeaking,
    config,
    audioLevel,
    recordingError,
    alwaysListening,

    // Audio device selection
    audioInputDevices,
    selectedAudioDeviceId,
    selectedAudioDeviceInfo,
    bluetoothMicLikelyLowQuality,
    loadAudioDevices,
    setAudioDevice,
    preferBuiltInMicForQuality,

    // Recording quality & mute
    recordingQuality,
    muteOnRecord,
    setRecordingQuality,
    setMuteOnRecord,

    // Wake word state
    wakeWordEnabled,
    wakeWordActive,
    wakeWordDetected,
    wakeWordListening,
    wakeWordProcessing,
    wakeWordAudioLevel,
    wakeWordMicWarning,

    // Dog commands
    dogCommand,

    // Computed
    hasMessages,
    lastMessage,

    // Actions
    initialize,
    sendMessage,
    sendVoiceInput,
    startListening,
    stopListening,
    addMessage,
    clearConversation,
    loadConversation,
    loadHistory,
    updateConfig,
    loadConfig,
    promptHistory,
    pushToHistory,
    resetProcessing,
    cancelRecording,
    cancelProcessing,
    enableAlwaysListening,
    disableAlwaysListening,
    enableWakeWord,
    disableWakeWord,
    cancelWakeWordCapture,
  };
});
